import { Button, Pagination } from "@heroui/react";
import { type LoaderFunctionArgs, json } from "@remix-run/cloudflare";
import { useLoaderData, useNavigate } from "@remix-run/react";
import dayjs from "dayjs";
import { and, eq, ne } from "drizzle-orm";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Search } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import csvIcon from "~/assets/icons/csvIcon.svg";
import pdfIcon from "~/assets/icons/pdfIcon.svg";
import DataTable, { createDataTableRow } from "~/components/table/data-table";
import { filterSearch, getListQuery } from "~/components/table/query";
import useListQuery from "~/components/table/use-list-query";
import Input from "~/components/ui/input";
import Select from "~/components/ui/select";
import { pins } from "~/db/schema/pins";
import { projects } from "~/db/schema/projects";
import { EFilterProjectStatus, EProjectStatus } from "~/enums/EProjectStatus";
import type { CloudflareENV } from "~/lib/interfaces";
import {
	type Province,
	getDistrictLabel,
	getProvinceLabel,
} from "~/lib/location";
import ProjectService from "~/services/db/project";
import projectSectors, { getProjectSectorsName } from "~/utils/projectSectors";
import type { ProjectFilter } from "./_admin.admin.projects._index";

export async function loader({ request, context }: LoaderFunctionArgs) {
	const env = context.cloudflare.env as CloudflareENV;

	const { page, limit, search, sort, sector, status, province, district } =
		getListQuery<ProjectFilter>(request);

	const filter = and(
		filterSearch(search, [projects.id, pins.name]),
		ne(projects.status, EProjectStatus.Draft),
		status !== "all" && status ? eq(projects.status, status) : undefined,
		Number(sector) !== 6 && sector
			? eq(pins.sector_id, Number(sector))
			: undefined,
		province ? eq(pins.province, province) : undefined,
		district ? eq(pins.district, district) : undefined,
	);

	const projectService = new ProjectService(env.DB);

	const provincesResponse = await fetch(new URL("/province.json", request.url));
	const provincesData = (await provincesResponse.json()) as Province[];

	const { total, result } = await projectService.paginateTable({
		filter,
		page,
		limit,
		sort,
	});
	return json({ result, total, provincesData });
}

export default function Projects() {
	const { result, total, provincesData } = useLoaderData<typeof loader>();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const rows = createDataTableRow<typeof result>([
		{
			key: "id",
			headerLabel: "ID",
			renderCell: (row) => (
				<div>{row.pins?.at(-1)?.verra_id ?? row?.public_id ?? row.id}</div>
			),
		},
		{
			key: "name",
			headerLabel: "Project Title",
			renderCell: (row) => <div>{row?.pins.at(-1)?.name}</div>,
			allowSorting: true,
		},
		{
			key: "sector",
			headerLabel: "Sector",
			renderCell: (row) => (
				<div>{getProjectSectorsName(row?.pins.at(-1)?.sector_id)}</div>
			),
		},
		{
			key: "City/Province",
			headerLabel: "Province",
			renderCell: (row) => (
				<div>
					{getProvinceLabel(provincesData, row?.pins?.at(-1)?.province || "")}
				</div>
			),
		},
		{
			key: "District",
			headerLabel: "District",
			renderCell: (row) => (
				<div>
					{getDistrictLabel(
						provincesData,
						row?.pins?.at(-1)?.province || "",
						row?.pins?.at(-1)?.district || "",
					)}
				</div>
			),
		},
		{
			key: "created_at",
			headerLabel: "Creation Date",
			renderCell: (row) => (
				<div>{dayjs(row.created_at).format("DD MMM YYYY")}</div>
			),
			allowSorting: true,
		},
		{
			key: "project_period",
			headerLabel: "Start Date - End Date",
			renderCell: (row) => (
				<div>
					{dayjs(row.pins.at(-1)?.start_date).format("DD MMM YYYY")}
					&nbsp;-&nbsp;
					{dayjs(row.pins.at(-1)?.end_date).format("DD MMM YYYY")}
				</div>
			),
		},
		{
			key: "status",
			headerLabel: "Status",
			renderCell: (row) => (
				<div
					className={`capitalize px-2 py-1 rounded-full w-fit  min-w-44 text-end
							${
								row.status === EProjectStatus.Archived
									? "text-red-500 font-bold"
									: "text-green-500 font-bold"
							}`}
				>
					{t(row.status).toUpperCase()}
				</div>
			),
		},
	]);

	const searchRef = useRef<HTMLInputElement>(null);

	const { query, updateQuery, sortDescriptor, handleSortChange } =
		useListQuery<ProjectFilter>();

	const [selectedProvince, setSelectedProvince] = useState<string | null>(null);

	const provinceOptions = useMemo(
		() =>
			provincesData.map((p) => ({
				key: p.code,
				label: p.latin,
			})),
		[provincesData],
	);

	const districtOptions = useMemo(() => {
		if (!selectedProvince) {
			// If no province selected, show districts from all provinces
			return provincesData.flatMap((province) =>
				province.districts.map((d) => ({
					key: d.code,
					label: `${d.latin} (${province.latin})`,
				})),
			);
		}
		// Show districts only from selected province
		const province = provincesData.find((p) => p.code === selectedProvince);
		return (
			province?.districts.map((d) => ({ key: d.code, label: d.latin })) || []
		);
	}, [selectedProvince, provincesData]);

	const handleSearch = () => {
		if (searchRef.current) {
			updateQuery({
				search: searchRef.current.value ?? "",
				page: 1,
			});
		}
	};

	const handleDownloadCSV = async () => {
		// Fetch all projects with current filters (no pagination)
		const params = new URLSearchParams({
			...(query.status ? { status: String(query.status) } : {}),
			...(query.sector ? { sector: String(query.sector) } : {}),
			...(query.province ? { province: String(query.province) } : {}),
			...(query.district ? { district: String(query.district) } : {}),
			...(query.search ? { search: String(query.search) } : {}),
		});

		const res = await fetch(`/projects/export?${params.toString()}`);
		const { result: allProjects } = (await res.json()) as {
			result: typeof result;
		};
		const data = allProjects.map((row) => ({
			ID: row.pins?.at(-1)?.verra_id ?? row?.public_id ?? row.id,
			Title: row?.pins.at(-1)?.name,
			Sector: getProjectSectorsName(row?.pins.at(-1)?.sector_id) ?? "",
			"Creation Date": dayjs(row.created_at).format("DD MMM YYYY"),
			"Start Date": dayjs(row.pins.at(-1)?.start_date).format("DD MMM YYYY"),
			"End Date": dayjs(row.pins.at(-1)?.end_date).format("DD MMM YYYY"),
			Status: row.status,
		}));
		const worksheet = XLSX.utils.json_to_sheet(data);
		const csv = XLSX.utils.sheet_to_csv(worksheet);
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "projects.csv";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const handleDownloadPDF = async () => {
		const params = new URLSearchParams({
			...(query.status ? { status: String(query.status) } : {}),
			...(query.sector ? { sector: String(query.sector) } : {}),
			...(query.province ? { province: String(query.province) } : {}),
			...(query.district ? { district: String(query.district) } : {}),
			...(query.search ? { search: String(query.search) } : {}),
		});

		const res = await fetch(`/projects/export?${params.toString()}`);
		const json = await res.json();
		const { result: allProjects } = json as { result: typeof result };
		const data = allProjects.map((row) => [
			row.pins?.at(-1)?.verra_id ?? row?.public_id ?? row.id ?? "",
			row?.pins.at(-1)?.name ?? "",
			getProjectSectorsName(row?.pins.at(-1)?.sector_id) ?? "",
			dayjs(row.created_at).format("DD MMM YYYY") ?? "",
			dayjs(row.pins.at(-1)?.start_date).format("DD MMM YYYY") ?? "",
			dayjs(row.pins.at(-1)?.end_date).format("DD MMM YYYY") ?? "",
			row.status ?? "",
		]);

		const doc = new jsPDF();
		const headers = [
			[
				"ID",
				"Title",
				"Sector",
				"Creation Date",
				"Start Date",
				"End Date",
				"Status",
			],
		];
		if (data.length === 0) {
			doc.text("No data to export.", 10, 10);
		} else {
			autoTable(doc, {
				head: headers,
				body: data,
				startY: 10,
				styles: { fontSize: 8 },
			});
		}
		doc.save("projects.pdf");
	};

	return (
		<div className="container mx-auto space-y-4 p-5">
			<div className="bg-[#F5F5F5] p-4 rounded-lg mt-4 text-blue-700 text-xl font-semibold">
				This page is under construction and should not be used for any reference. It does not contain any official information.
			</div>
			<div className="grid grid-cols-1 md:flex space-x-0 md:space-x-3 justify-end mb-5 space-y-2 md:space-y-0 items-center">
				<div className="md:w-40">
					<Select
						onChange={(e) => {
							updateQuery({
								status: e.target.value as EFilterProjectStatus,
								page: 1,
							});
						}}
						placeholder="Status"
						selectedKeys={[query.status]}
						data={Object.values(EFilterProjectStatus).map((item) => ({
							value: item,
							label: item,
							key: item,
						}))}
					/>
				</div>
				<div className="md:w-80">
					<Select
						onChange={(e) => {
							updateQuery({
								sector: e.target.value as EProjectStatus,
								page: 1,
							});
						}}
						placeholder="Project Sector"
						selectedKeys={[query.sector].toString()}
						data={[{ key: "6", name: "All" }, ...projectSectors].map(
							(item) => ({
								value: item.name,
								label: item.name,
								key: item.key,
							}),
						)}
					/>
				</div>
				<div className="w-60">
					<Select
						name="province"
						placeholder="Select Province"
						selectedKeys={[query.province]}
						data={provinceOptions}
						onChange={(e) => {
							const provinceCode = e.target.value;
							setSelectedProvince(provinceCode);
							updateQuery({ province: provinceCode, district: "", page: 1 });
						}}
					/>
				</div>
				<div className="w-60">
					<Select
						name="district"
						placeholder="Select District"
						selectedKeys={[query.district]}
						data={districtOptions}
						onChange={(e) => {
							updateQuery({ district: e.target.value, page: 1 });
						}}
					/>
				</div>
				<div>
					<div className="space-x-3 flex items-center">
						<Input
							ref={searchRef}
							defaultValue={query.search as string}
							placeholder="Search ..."
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									handleSearch();
								}
							}}
							endContent={
								<div>
									<Button
										variant="flat"
										className="bg-transparent hover:bg-transparent"
										isIconOnly
										onPress={handleSearch}
									>
										<Search
											onClick={() => {
												updateQuery({
													search: searchRef.current?.value ?? "",
													page: 1,
												});
											}}
										/>
									</Button>
								</div>
							}
						></Input>
					</div>
				</div>
				<Button isIconOnly style={{ background: "transparent" }}>
					<img
						onClick={handleDownloadCSV}
						src={csvIcon}
						alt="PDF"
						width={"35px"}
						height={"35px"}
						style={{ objectFit: "fill" }}
					/>
				</Button>
				<Button isIconOnly style={{ background: "transparent" }}>
					<img
						onClick={handleDownloadPDF}
						src={pdfIcon}
						alt="PDF"
						width={"35px"}
						height={"35px"}
						style={{ objectFit: "fill" }}
					/>
				</Button>
			</div>
			<DataTable
				data={result}
				rows={rows}
				onSortChange={handleSortChange}
				sortDescriptor={sortDescriptor}
				// onRowClick={id => {
				//   navigate(`/projects/${id}`)
				// }}
			/>
			{total > 0 && (
				<div className="flex justify-center">
					<Pagination
						showControls
						page={Number(query.page)}
						total={Math.ceil(total / Number(query.limit))}
						onChange={(page) => {
							updateQuery({ page });
						}}
					/>
				</div>
			)}
		</div>
	);
}
